'use client';

import toast from '@shared/utils/helpers/toast';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';

import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import { Pill, SOURCE_LABEL } from './common';

// One synced document, read: its stored transcript page by page, plus a
// download that redirects to a short-lived OneDrive URL.
//
// Extracted from SearchPanel when the Factiiis tab needed the same view. The
// filter there lists files, and a file you cannot open is a dead end. Both
// callers hold only `{ uploadId, name }`, so the hydration lives in here.

export type DocumentTarget = { uploadId: string; name: string | null };

const DocumentModal = ({
  target,
  closeHandler
}: {
  target: DocumentTarget;
  closeHandler: () => void;
}) => {
  // Same route the AI calls (batched). Here it pulls one document for a human.
  const contextQ = useQuery({
    queryKey: ['onedriveEmbed', 'context', target.uploadId],
    queryFn: async () => {
      const [err, res] = await client.onedriveEmbed.context.query({
        uploadIds: [target.uploadId]
      });
      if (err) throw err;
      return res.documents[0] ?? null;
    }
  });
  const doc = contextQ.data;

  const download = async () => {
    const [err, res] = await client.onedriveEmbed.downloadUrl.mutate({
      uploadId: target.uploadId
    });
    if (err) {
      toast(err.message || 'Could not get download link', '', 'error');
      return;
    }
    window.open(res.url, '_blank', 'noopener');
  };

  return (
    <Modal maxWidth={800} closeHandler={closeHandler}>
      <ModalHeader closeHandler={closeHandler}>
        <span className="flex min-w-0 items-center gap-2">
          <FileText size={18} className="shrink-0 text-primary" />
          <span className="truncate">
            {target.name ?? doc?.name ?? 'Document'}
          </span>
        </span>
      </ModalHeader>
      <ModalContent>
        {contextQ.isFetching ? (
          <div className="flex items-center gap-2 py-6 text-sm text-textSecondary">
            <Loader2 size={16} className="animate-spin" /> Loading context…
          </div>
        ) : doc ? (
          <>
            <div className="mb-4 flex flex-wrap gap-1">
              <Pill>{doc.pages.length} pages</Pill>
              <Pill>{doc.chunkCount} chunks</Pill>
              <Pill>{doc.source}</Pill>
            </div>
            {doc.pages.length === 0 ? (
              <p className="text-sm text-textSecondary">
                No transcript text stored for this document.
              </p>
            ) : (
              <div className="space-y-4">
                {doc.pages.map((pg) => (
                  <div key={pg.page}>
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <span className="font-medium text-textPrimary">
                        Page {pg.page}
                      </span>
                      {/* Null for anything synced before the source moved onto
                          the upload. Show nothing rather than a pill labelled
                          "null". */}
                      {pg.source && (
                        <Pill>{SOURCE_LABEL[pg.source] ?? pg.source}</Pill>
                      )}
                    </div>
                    <pre className="whitespace-pre-wrap rounded bg-background-primary p-3 text-xs text-textSecondary">
                      {pg.text}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="py-6 text-sm text-textSecondary">
            Couldn’t load context for this document.
          </p>
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          variant="SECONDARY"
          text="Download"
          onClick={() => void download()}
        />
        <Button variant="PRIMARY" text="Close" onClick={closeHandler} />
      </ModalFooter>
    </Modal>
  );
};

export default DocumentModal;
